import {Component, EventEmitter, Input, OnChanges, Output} from '@angular/core';
import {IoniconsOutline, SessionInfoType, SessionNotification, SessionNotificationStatus} from '@pt/pt-models';
import {TranslateService} from '@ngx-translate/core';
import {Observable, of} from 'rxjs';
import {animate, state, style, transition, trigger} from '@angular/animations';

@Component({
  selector: 'pt-session-notification-item',
  animations: [
    trigger('collapsable', [
      state('opened', style({
        height: '*'
      })),
      state('closed', style({
        height: 0,
        padding: 0,
        overflow: 'hidden',
        background: 'red',
        margin: 0
      })),
      transition('opened <=> closed', [
        animate('0.7s cubic-bezier(0.77, 0, 0.175, 1)')
      ])
    ])
  ],
  template: `
    <div class='session-notification'
         [@collapsable]='state'
         [style.border-color]="statusColor"
         [class.session-notification--closable]="closeFocus"
    >
      <pt-icon class='close hstack'
               [size]='20'
               [key]='IconClose'
               (mouseenter)='closeFocus = true'
               (mouseleave)='closeFocus = false'
               (click)='clickClose(); $event.stopPropagation();'
      ></pt-icon>

      <div class='image'>
        <pt-icon class='notification-status' container='body'
                 [ngbTooltip]='statusTooltip | async'
                 [ngClass]='statusClass'
                 [key]='statusIcon'
                 [size]='15'
                 *ngIf="statusIcon"
        ></pt-icon>
        <pt-icon
          class='notification-type'
          [ngClass]='statusClass'
          [key]='notificationIcon'
          [size]='30'
          *ngIf="notificationIcon"
        ></pt-icon>
      </div>

      <div class='body'>
        <div class='card-body w-100'>
          <div class='h5 card-title text-ellipsis' container='body'
                [ngbTooltip]='title' [innerHTML]='title' *ngIf='title'
          ></div>
          <p class='card-text text-ellipsis m-0'
             [innerHTML]='description' *ngIf='description'
          ></p>
          <button class='btn btn-sm btn-outline-primary'
                  *ngIf='isClickable' (click)='openNotification.emit()'
          >{{ "BUTTON.DOWNLOAD" | translate }}
          </button>
          <p class='card-text text-end m-0' *ngIf='stateLabel && date'>
            <small class='text-muted'>
              {{ stateLabel | async }} {{ date | latestChangeDate }}
            </small>
          </p>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .session-notification {
      display: flex;
      position: relative;
      margin-bottom: 10px;
      align-items: center;
      background-color: #fbfbfb;
      justify-content: flex-start;
      border-left: 3px solid transparent;
    }

    .session-notification--clickable:hover {
      cursor: pointer;
      border-color: var(--bs-secondary);
      background-color: rgba(108, 117, 125, .1);
    }

    .session-notification--closable:hover {
      border-color: var(--bs-danger);
      background-color: rgba(220, 53, 69, .1);
    }

    .session-notification .close {
      position: absolute;
      right: 2px;
      top: 2px;
      cursor: pointer;
    }

    .session-notification .image {
      position: relative;
      width: 50px;
      display: inline-flex;
      justify-content: center;
      align-items: flex-start;
      padding: 10px 5px;
    }

    .session-notification .image .notification-status {
      position: absolute;
      top: 2px;
      right: 2px;
      padding: 0;
      border-radius: 20px;
    }

    .session-notification .image .notification-type {
      margin-top: 3px;
    }

    .session-notification .body {
      width: calc(100% - 50px - 35px);
      display: inline-flex;
      justify-content: flex-start;
      margin-left: 5px;
      padding: 5px;
    }

    .session-notification .card-body {
      padding: 0;
    }

    .session-notification .card-body .card-title {
      font-size: 16px;
      margin-bottom: 5px;
      max-width: 250px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .session-notification .card-body .card-text {
      font-size: 12px;
      margin-bottom: 5px;
    }
  `]
})
export class SessionNotificationItemComponent implements OnChanges {
  @Input() sessionNotification: SessionNotification;
  @Output() closeSessionNotification = new EventEmitter();
  @Input() index: number;

  @Output() openNotification = new EventEmitter();
  state = 'opened';
  stateLabel: Observable<string> = this.translate.stream('LABELS.OPENED');
  statusClass = 'text-muted';
  statusColor = '#afafaf';
  statusIcon?: string = IoniconsOutline.infoCircle;
  notificationIcon: string = IoniconsOutline.infoCircle;
  title = 'Loading ...';
  description = 'Loading ...';
  date: number = Date.now();
  statusTooltip: Observable<string> = of('Loading ...');
  isClickable = false;
  closeFocus = false;
  IconClose = IoniconsOutline.close;

  constructor(private translate: TranslateService) {
  }

  ngOnChanges(): void {
    this.initItem();
  }

  clickClose() {
    this.state = 'closed';
    this.stateLabel = this.translate.stream('LABELS.CLOSED');

    setTimeout(() => this.closeSessionNotification.emit(), 700);
  }

  private initItem() {
    if (this.sessionNotification) {
      this.description = this.sessionNotification.description;

      switch (this.sessionNotification.status) {
        case SessionNotificationStatus.COMPLETED:
          this.statusClass = 'text-success';
          this.statusColor = '#6bb700';
          this.statusIcon = IoniconsOutline.check;
          this.statusTooltip = this.translate.stream('TOOLTIP.COMPLETED');
          break;

        case SessionNotificationStatus.PARTIAL_COMPLETED:
          this.statusClass = 'text-warning';
          this.statusColor = '#eaa546';
          this.statusIcon = IoniconsOutline.infoCircle;
          this.statusTooltip = this.translate.stream('TOOLTIP.COMPLETED');
          break;

        case SessionNotificationStatus.ERROR:
          this.statusClass = 'text-danger';
          this.statusColor = '#ea4646';
          this.statusIcon = IoniconsOutline.infoCircle;
          this.statusTooltip = this.translate.stream('TOOLTIP.ERROR');
          break;
        case SessionNotificationStatus.RUNNING:
          this.statusClass = 'text-primary';
          this.statusColor = '#0369c8';
          this.statusIcon = IoniconsOutline.play;
          this.statusTooltip = this.translate.stream('TOOLTIP.RUNNING');
          break;
        case SessionNotificationStatus.STOPPED:
          this.statusClass = 'text-warning';
          this.statusColor = '#eaa546';
          this.statusIcon = IoniconsOutline.circleStop;
          this.statusTooltip = this.translate.stream('TOOLTIP.STOPPED');
          break;
        case SessionNotificationStatus.INFO:
          this.statusClass = 'text-info';
          this.statusColor = '#0369c8';
          this.statusIcon = IoniconsOutline.infoCircle;
          this.statusTooltip = this.translate.stream('TOOLTIP.INFO');
          break;
        case SessionNotificationStatus.SUCCESS:
          this.statusClass = 'text-success';
          this.statusColor = '#6bb700';
          this.statusIcon = IoniconsOutline.check;
          this.statusTooltip = this.translate.stream('TOOLTIP.SUCCESS');
          break;

        case SessionNotificationStatus.WARNING:
          this.statusClass = 'text-warning';
          this.statusColor = '#eaa546';
          this.statusIcon = IoniconsOutline.infoCircle;
          this.statusTooltip = this.translate.stream('TOOLTIP.WARNING');
          break;
      }

      switch (this.sessionNotification.actionType) {
        case SessionInfoType.DOWNLOAD:
          this.title = this.sessionNotification.name ?? ("Download " + (this.index + 1));
          this.notificationIcon = IoniconsOutline.download;
          this.isClickable =
            this.sessionNotification.status === SessionNotificationStatus.COMPLETED ||
            this.sessionNotification.status === SessionNotificationStatus.PARTIAL_COMPLETED;
          break;
        case SessionInfoType.NOTIFICATION:
          this.statusIcon = undefined;
          this.title = this.sessionNotification.name;
          this.notificationIcon = IoniconsOutline.infoCircle;
          this.notificationIcon =
            this.sessionNotification.status === SessionNotificationStatus.SUCCESS ?
              IoniconsOutline.circleCheck : IoniconsOutline.infoCircle;
          this.isClickable = false;
          break;
      }
    }
  }
}
